import { useState, useRef, useEffect } from 'react';

import "./messagingInputFooter.css";
import { VscSend } from "react-icons/vsc";
import CountdownTimer from "../helpers/countdownTimer";
import { util } from "../helpers/common";
import { CONVERSATION_CONSTANTS, CLIENT_CONSTANTS } from '../helpers/constants';
import { getConversationId } from '../services/dataProvider';
import { t, useAwacLang, getLang } from '../awac/i18n';
import { getPageContext } from '../awac/pageContext';

// Suggested prompts shown above the input — PAGE-AWARE quick actions: on the
// Neighborhood page they become map questions, on the Units page unit
// questions. Resolved lazily so they follow the active page and language.
function getSuggestedPrompts() {
    const page = getPageContext().split('/')[0];
    if (page === 'neighborhood') {
        return [t('mapChips.1'), t('mapChips.2'), t('mapChips.3')];
    }
    if (page === 'units') {
        return [t('chat.suggestionUnits'), t('chat.suggestion1')];
    }
    return [t('chat.suggestion1'), t('chat.suggestion2')];
}

// Maximum height (px) the input textarea can grow to before scrolling.
const TEXTAREA_MAX_HEIGHT = 120;

export default function MessagingInputFooter(props) {
    // Initialize the Textarea value to empty.
    let [textareaContent, setTextareaContent] = useState('');
    // Re-render when the page language changes (FR default, Bill 96).
    useAwacLang();

    // Ref to the input textarea, used for auto-growing its height.
    const textareaRef = useRef(null);

    // Auto-grow the textarea with its content, capped at TEXTAREA_MAX_HEIGHT.
    // When empty, leave the height fully CSS-controlled (min-height) so the
    // placeholder can never be clipped by a stale measured height (e.g. when
    // fonts load after the first render). The scrollbar only appears once
    // the content actually exceeds the max height.
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }
        if (!textareaContent) {
            textarea.style.height = '';
            textarea.style.overflowY = 'hidden';
            return;
        }
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
        textarea.style.overflowY = textarea.scrollHeight > TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';
    }, [textareaContent]);

    // Whether the suggested-prompt chips are dismissed by the user.
    let [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
    // Track the current page so the quick actions swap (and re-appear) when
    // the visitor — or the agent — navigates to another page.
    let [, setSuggestionPage] = useState(getPageContext().split('/')[0]);

    useEffect(() => {
        function onHashChange() {
            const next = getPageContext().split('/')[0];
            setSuggestionPage((prev) => {
                if (prev !== next) {
                    setSuggestionsDismissed(false);
                }
                return next;
            });
        }
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    // Initialize whether end user is actively typing. 
    // This holds a reference to a CountdownTimer object.
    let [typingIndicatorTimer, setTypingIndicatorTimer] = useState(undefined);

    /**
     * Handle 'change' event in Textarea to reactively update the Textarea value.
     * @param {object} event
     */
    function handleTextareaContentChange(event) {
        if (event && event.target && typeof event.target.value === "string") {
            setTextareaContent(event.target.value);
        }

        // Handle when end user is actively typing.
        if (textareaContent !== "" && textareaContent.length !== 0) {
            // If the end user has typed within the last 5 seconds, reset the timer for another 5 seconds.
            // Otherwise, send a new started typing indicator and start a new countdown.
            if (typingIndicatorTimer) {
                typingIndicatorTimer.reset(Date.now());
            } else {
                handleSendTypingIndicator(CONVERSATION_CONSTANTS.EntryTypes.TYPING_STARTED_INDICATOR);
                typingIndicatorTimer = new CountdownTimer(() => {
                    handleSendTypingIndicator(CONVERSATION_CONSTANTS.EntryTypes.TYPING_STOPPED_INDICATOR);
                    typingIndicatorTimer = undefined;
                }, CLIENT_CONSTANTS.TYPING_INDICATOR_DISPLAY_TIMEOUT, Date.now());
                typingIndicatorTimer.start();
            }
        }
    }

    /**
     * Handle 'key' event in Textarea. If the key is 'Enter', send a message.
     * @param {object} event
     */
    function handleTextareaKeyChange(event) {
        if (event.key === "Enter" && !event.altKey) {
            event.preventDefault();
            
            handleSendButtonClick();
        }
    }

    /**
     * Handle 'click' event in Textarea to put focus on Textarea.
     * @param {object} event
     */
    function handleTextareaClick(event) {
        if (event) {
            event.target.focus();
        }
    }

    /**
     * Clears the Textarea i.e. resets to empty.
     */
    function clearMessageContent() {
        setTextareaContent("");
    }

    /**
     * Determines whether to disable the Textarea.
     * TRUE - disables if the conversation is either closed or not started and FALSE - otherwise.
     */
    function shouldDisableTextarea() {
        return props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION || props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION;
    }

    /**
     * Determines whether to disable the Send Button.
     * TRUE - disables if the Textarea is either empty or if the conversation is not open and FALSE - otherwise.
     */
    function shouldDisableSendButton() {
        return textareaContent.trim().length === 0 || props.conversationStatus !== CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION;
    }

    /**
     * Handle Send Button click. If the Button is enabled, send a message.
     */
    function handleSendButtonClick() {
        if (!shouldDisableSendButton()) {
            handleSendMessage();
        }
    }

    /**
     * Sends a suggested-prompt chip as a message (if the conversation is open).
     * @param {string} prompt
     */
    function handleSuggestionClick(prompt) {
        if (props.conversationStatus !== CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
            return;
        }
        const conversationId = getConversationId();
        const messageId = util.generateUUID();
        props.sendTextMessage(conversationId, prompt, messageId)
            .then(() => {
                console.log(`Successfully sent suggested prompt to conversation: ${conversationId}`);
            });
        setSuggestionsDismissed(true);
    }

    /**
     * Whether to show the suggested-prompt chips. They are ONBOARDING aids:
     * shown only on an open conversation where the visitor has not yet sent
     * anything. Once the conversation is flowing, the agent's own option
     * buttons (inside its replies) are the single quick-action surface — no
     * duplicated chips at the bottom.
     */
    function shouldShowSuggestions() {
        return !suggestionsDismissed
            && !props.hasUserMessaged
            && props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION;
    }

    /**
     * Handle sending a text message by generating a new unique message-id and invoke the parent's handler to send a message with the typed text.
     */
    function handleSendMessage() {
        // Required parameters.
        const conversationId = getConversationId();
        const messageId = util.generateUUID();
        const value = textareaContent;
        // Optional parameters.
        let inReplyToMessageId;
        let isNewMessagingSession;
        let routingAttributes;
        // Carry the CURRENT site language on every send — the conversation is
        // pinned at creation, but a visitor who toggles FR/EN mid-chat should
        // have the change reach the session, not just future conversations.
        const language = getLang() === "fr" ? "fr_CA" : "en_US";

        props.sendTextMessage(conversationId, value, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language)
            .then(() => {
                console.log(`Successfully sent a text message to conversation: ${conversationId}`);
                // Clear textarea value.
                clearMessageContent();
            });
    }

    /**
     * Handle calling a sendTypingIndicator when the timer fires with started/stopped indicator.
     * @param {string} typingIndicator - whether to send a typing started or stopped indicator.
     */
        function handleSendTypingIndicator(typingIndicator) {
            const conversationId = getConversationId();
    
            props.sendTypingIndicator(conversationId, typingIndicator)
            .then(() => {
                console.log(`Successfully sent ${typingIndicator} to conversation: ${conversationId}`);
            })
            .catch(error => {
                console.error(`Something went wrong while sending a typing indicator to conversation ${conversationId}: ${error}`);
            });
        }

    return(
        <div className="messagingFooter">
            {shouldShowSuggestions() &&
                <div className="messagingSuggestions">
                    <div className="messagingSuggestionsHeader">
                        <span className="messagingSuggestionsLabel">{t("chat.suggestionsLabel")}</span>
                        <button
                            type="button"
                            className="messagingSuggestionsDismiss"
                            aria-label={t("chat.suggestionsDismiss")}
                            title={t("chat.suggestionsDismiss")}
                            onClick={() => setSuggestionsDismissed(true)}>
                            &times;
                        </button>
                    </div>
                    <div className="messagingSuggestionsChips">
                        {getSuggestedPrompts().map((prompt) => (
                            <button
                                key={prompt}
                                type="button"
                                className="messagingSuggestionChip"
                                onClick={() => handleSuggestionClick(prompt)}>
                                {prompt}
                            </button>
                        ))}
                    </div>
                </div>
            }
            <div className="messagingInputRow">
                <textarea className="messagingInputTextarea" 
                    ref={textareaRef}
                    placeholder={t("chat.inputPlaceholder")}
                    value={textareaContent}
                    onChange={handleTextareaContentChange}
                    onKeyDown={handleTextareaKeyChange}
                    onClick={handleTextareaClick}
                    rows={1}
                    disabled={shouldDisableTextarea()} />

                <button className="sendButton"
                    onClick={handleSendButtonClick}
                    aria-label={t("chat.send")}
                    title={t("chat.send")}
                    disabled={shouldDisableSendButton()}>
                    <VscSend className="sendButtonIcon" />
                </button>
            </div>
            <p className="messagingFooterTerms">
                {t("chat.terms")}
            </p>
        </div>
    );
}